// Schematic single-stream DiT, informed by Krea 2's public report:
// https://www.krea.ai/blog/krea-2-technical-report
// Repeated planes are illustrative, not a model-specific layer count.
import { projector, renderCurves } from './research-wireframes.js';
const line = (points, extra = {}) => ({ points, ...extra });
const rect = (x,y,w,h,z) => [[x,y,z],[x+w,y,z],[x+w,y+h,z],[x,y+h,z],[x,y,z]];
function box(x,y,w,h,z,depth,extra = {}) {
    const front = rect(x,y,w,h,z), back = rect(x,y,w,h,z+depth);
    return [line(front,extra),line(back,extra),...front.slice(0,4).map((p,i)=>line([p,back[i]],extra))];
}
export function ditGeometry(small = false) {
    const curves = [], labels = [];
    const label = (text,x,y,z=0) => labels.push({text,point:[x,y,z]});
    const grid = (x,y,w,h,cols,rows) => {
        curves.push(...box(x,y,w,h,0,15));
        for(let c=1;c<cols;c++) curves.push(line([[x+w*c/cols,y,0],[x+w*c/cols,y+h,0]]));
        for(let r=1;r<rows;r++) curves.push(line([[x,y+h*r/rows,0],[x+w,y+h*r/rows,0]]));
    };
    const route = (points,offset=0) => {
        curves.push(line(points));
        const end=points.at(-1), before=points.at(-2);
        const dx=end[0]-before[0],dy=end[1]-before[1],length=Math.hypot(dx,dy),ux=dx/length,uy=dy/length;
        curves.push(line([[end[0]-ux*9+uy*4,end[1]-uy*9-ux*4,end[2]],end,[end[0]-ux*9-uy*4,end[1]-uy*9+ux*4,end[2]]]));
        for (let packet = 0; packet < 3; packet++) {
            curves.push(...box(-6,-6,12,12,0,8,{
                travel:{route:points,period:12,offset:offset+packet*4},
            }));
        }
    };
    if(small) {
        grid(-250,-245,155,65,6,2); grid(90,-255,125,95,5,4);
        label('TEXT',-172,-275); label('IMAGE',152,-285);
        grid(-140,-75,280,45,14,1);
        route([[-172,-180,0],[-172,-125,0],[-70,-125,0],[-70,-75,0]]);
        route([[152,-160,0],[152,-125,0],[70,-125,0],[70,-75,0]]);
        route([[0,-30,0],[0,48,80]],3);
        grid(-285,95,75,35,3,1);
        route([[-210,112,0],[-115,112,80]],3);
        grid(-70,330,140,80,7,4);
        route([[0,232,80],[0,330,0]],7);
    } else {
        grid(-505,-125,150,65,6,2); grid(-495,70,135,110,5,4);
        label('TEXT',-430,-145); label('IMAGE',-428,200);
        grid(-260,-82,64,164,2,8);
        route([[-355,-92,0],[-300,-92,0],[-300,-40,0],[-260,-40,0]]);
        route([[-360,125,0],[-300,125,0],[-300,40,0],[-260,40,0]]);
        route([[-196,0,0],[-105,0,80]],3);
        grid(-35,-205,120,30,5,1);
        route([[25,-175,0],[25,-112,80]],3);
        grid(325,-60,125,120,5,5);
        route([[125,0,80],[325,0,0]],7);
    }
    const x=small?-115:-105,y=small?48:-112,w=230,h=small?184:224;
    for(const z of [0,32,64]) curves.push(...box(x,y,w,h,z,16));
    // Two fields of square tokens replace the text-heavy sublayer boxes.
    // A staggered wave carries selected squares through the stacked planes.
    for (let band = 0; band < 2; band++) {
        const top = band === 0 ? y + 22 : y + h - 77;
        for (let row = 0; row < 2; row++) for (let col = 0; col < 6; col++) {
            const left = x + 18 + col * 29, yy = top + row * 29;
            curves.push(line(rect(left,yy,22,22,80)));
            if ((col + row + band) % 2 === 0) {
                const route = [[left,yy,0],[left,yy,80],[left,yy,0]];
                curves.push(...box(0,0,22,22,0,5,{
                    travel:{route,period:10,offset:col*.8+row*1.3+band*2},
                }));
            }
        }
    }
    label('DiT',x+w/2,y+h+36);
    // Residual path around the attention and MLP sublayers.
    curves.push(line([[x+w-27,y+10,80],[x+w-10,y+10,80],[x+w-10,y+h-10,80],[x+w-27,y+h-10,80]]));
    return {curves,labels};
}
export function createDitScene() {
    const desktop=ditGeometry(),mobile=ditGeometry(true);
    return {
        labels:desktop.labels,
        draw(seconds,small) {
            const geometry=small?mobile:desktop,time=seconds*.35;
            const project=projector({time,phase:.8,x:small?380:610,y:small?270:228,scale:small?.69:.82,depth:.85});
            return {
                path:renderCurves(geometry.curves,project,seconds*.7),
                labels:geometry.labels.map(label=>{
                    const [x,y]=project(label.point); return {text:label.text,x,y};
                }),
            };
        },
    };
}
